<template>
  <v-container class="py-6" max-width="1200">
    <!-- Header -->
    <div class="d-flex align-center mb-6">
      <v-btn icon variant="text" to="/admin" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-icon color="info" size="32" class="mr-3">mdi-brain</v-icon>
      <div>
        <h1 class="text-h5 font-weight-bold">AI Training</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Manage videos, frames and dice models</p>
      </div>
      <v-spacer />
      <v-btn
        v-if="!trainingRunning"
        color="info"
        variant="flat"
        class="mr-2"
        @click="showTrainDialog = true"
      >
        <v-icon start>mdi-brain</v-icon>
        Start Training
      </v-btn>
      <v-btn
        v-else
        color="error"
        variant="flat"
        :loading="stoppingTraining"
        class="mr-2"
        @click="handleStopTraining"
      >
        <v-icon start>mdi-stop</v-icon>
        Stop Training
      </v-btn>
      <v-btn
        v-if="trainingLog.length > 0"
        variant="outlined"
        class="mr-2"
        @click="showTrainLog = true"
      >
        <v-icon start>mdi-console</v-icon>
        View Log
      </v-btn>
      <v-btn color="success" variant="flat" :loading="exporting" @click="handleExport">
        <v-icon start>mdi-export</v-icon>
        Export for YOLO
      </v-btn>
    </div>

    <!-- Training Progress Banner -->
    <v-alert
      v-if="trainingRunning"
      type="info"
      variant="tonal"
      class="mb-6"
      prominent
    >
      <div class="d-flex align-center">
        <v-progress-circular indeterminate size="20" width="2" class="mr-3" />
        <div>
          <div class="font-weight-bold">Training in progress</div>
          <div class="text-body-2">
            Model: {{ trainingModel }} | Epochs: {{ trainingEpochs }} |
            Started: {{ trainingStartedAt ? formatDate(trainingStartedAt) : '...' }}
          </div>
          <div v-if="trainingLog.length > 0" class="text-caption mt-1" style="font-family: monospace; color: #aaa;">
            {{ trainingLog[trainingLog.length - 1] }}
          </div>
        </div>
        <v-spacer />
        <v-btn variant="outlined" size="small" @click="showTrainLog = true">
          View Log
        </v-btn>
      </div>
    </v-alert>

    <!-- Training Stats -->
    <v-row class="mb-6" v-if="stats">
      <v-col cols="6" md="2">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-3">
            <div class="text-h6 font-weight-bold">{{ stats.totalVideos }}</div>
            <div class="text-caption text-medium-emphasis">Videos</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="2">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-3">
            <div class="text-h6 font-weight-bold">{{ stats.totalFrames }}</div>
            <div class="text-caption text-medium-emphasis">Frames</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="2">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-3">
            <div class="text-h6 font-weight-bold text-success">{{ stats.labeledFrames }}</div>
            <div class="text-caption text-medium-emphasis">Labeled</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="2">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-3">
            <div class="text-h6 font-weight-bold text-warning">{{ stats.totalBoxes }}</div>
            <div class="text-caption text-medium-emphasis">Bounding boxes</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="4">
        <v-card variant="outlined">
          <v-card-text class="pa-3">
            <div class="text-caption text-medium-emphasis mb-1">Dice Distribution</div>
            <div class="d-flex ga-2">
              <v-chip v-for="(count, i) in stats.classCounts" :key="i" size="small" :color="diceColors[i]" variant="tonal">
                d{{ i + 1 }}: {{ count }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Pending Files -->
    <v-card v-if="pendingFiles.length > 0" variant="outlined" class="mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="warning">mdi-file-video-outline</v-icon>
        Pending video files
        <v-chip size="small" color="warning" variant="tonal" class="ml-2">{{ pendingFiles.length }}</v-chip>
      </v-card-title>
      <v-card-subtitle>Video files found in the folder that are not yet registered</v-card-subtitle>
      <v-card-text>
        <v-list density="compact">
          <v-list-item v-for="file in pendingFiles" :key="file">
            <template #prepend>
              <v-icon size="small" color="warning">mdi-video-outline</v-icon>
            </template>
            <v-list-item-title>{{ file }}</v-list-item-title>
            <template #append>
              <v-btn size="small" color="primary" variant="tonal" :loading="registeringFile === file" @click="handleRegister(file)">
                <v-icon start size="small">mdi-plus</v-icon>
                Register
              </v-btn>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <!-- Videos Table -->
    <v-card variant="outlined">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-video-outline</v-icon>
        Videos
        <v-spacer />
        <v-btn size="small" variant="text" @click="loadVideos">
          <v-icon start>mdi-refresh</v-icon>
          Update
        </v-btn>
      </v-card-title>
      <v-divider />

      <div v-if="loadingVideos" class="d-flex justify-center py-8">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <v-table v-else-if="videos.length > 0" density="comfortable">
        <thead>
          <tr>
            <th>ID</th>
            <th>Video</th>
            <th>Status</th>
            <th>Frames</th>
            <th>Labeled</th>
            <th>FPS</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="v in videos" :key="v.videoId">
            <td class="text-caption text-medium-emphasis">#{{ v.videoId }}</td>
            <td>
              <div class="font-weight-medium">{{ v.originalName }}</div>
              <div class="text-caption text-medium-emphasis" v-if="v.uploadedByName">
                by {{ v.uploadedByName }}
              </div>
            </td>
            <td>
              <v-chip size="small" :color="statusColor(v.status)" variant="tonal">
                {{ statusLabel(v.status) }}
              </v-chip>
            </td>
            <td>{{ v.frameCount }}</td>
            <td>
              <span :class="v.labeledCount > 0 ? 'text-success' : ''">{{ v.labeledCount }}</span>
              <span v-if="v.frameCount > 0" class="text-medium-emphasis">
                ({{ Math.round(v.labeledCount / v.frameCount * 100) }}%)
              </span>
            </td>
            <td>{{ v.fps }}</td>
            <td class="text-caption">{{ formatDate(v.createdAt) }}</td>
            <td>
              <div class="d-flex ga-1">
                <v-btn
                  v-if="v.status === 'pending'"
                  size="small"
                  color="primary"
                  variant="tonal"
                  :loading="processingVideoId === v.videoId"
                  @click="openProcessDialog(v)"
                >
                  <v-icon size="small">mdi-cog-play</v-icon>
                </v-btn>
                <v-btn
                  v-if="v.status === 'extracted'"
                  size="small"
                  color="success"
                  variant="tonal"
                  @click="handleMarkCompleted(v)"
                >
                  <v-icon size="small">mdi-check</v-icon>
                </v-btn>
                <v-btn
                  size="small"
                  color="error"
                  variant="text"
                  @click="openDeleteDialog(v)"
                >
                  <v-icon size="small">mdi-delete</v-icon>
                </v-btn>
              </div>
            </td>
          </tr>
        </tbody>
      </v-table>

      <v-card-text v-else class="text-center text-medium-emphasis py-8">
        No videos available yet. Upload a video or place it in the video folder.
      </v-card-text>
    </v-card>

    <!-- Test Model Section -->
    <v-card variant="outlined" class="mt-6">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-test-tube</v-icon>
        Test Model
        <v-spacer />
        <v-text-field
          v-model.number="testCount"
          label="Frames"
          type="number"
          min="1"
          max="50"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 100px"
          class="mr-2"
        />
        <v-text-field
          v-model.number="testConfidence"
          label="Confidence"
          type="number"
          min="0.1"
          max="0.99"
          step="0.05"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 120px"
          class="mr-2"
        />
        <v-btn color="info" variant="flat" :loading="testing" @click="handleRunTest">
          <v-icon start>mdi-play</v-icon>
          Run Test
        </v-btn>
      </v-card-title>
      <v-card-text v-if="testResults.length > 0">
        <div class="test-grid">
          <div v-for="img in testResults" :key="img.filename" class="test-image-card">
            <img :src="getTestImageSrc(img.filename)" :alt="img.filename" class="test-image" />
            <div class="test-image-name">{{ img.filename.substring(20) }}</div>
          </div>
        </div>
      </v-card-text>
      <v-card-text v-else-if="!testing" class="text-center text-medium-emphasis py-6">
        Run a test to see how the trained model performs on random frames.
      </v-card-text>
    </v-card>

    <!-- Process Dialog -->
    <v-dialog v-model="showProcessDialog" max-width="500">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-cog-play</v-icon>
          Extract Frames
        </v-card-title>
        <v-card-text>
          <p class="mb-4">Extract frames from <strong>{{ processTarget?.originalName }}</strong></p>
          <v-text-field
            v-model.number="processFps"
            label="Frames per second"
            type="number"
            min="0.1"
            max="30"
            step="0.5"
            hint="1 = one frame per second, 0.5 = one every 2 seconds"
            persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showProcessDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :loading="processingVideoId !== null" @click="handleProcess">
            <v-icon start>mdi-play</v-icon>
            Start
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="440">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert</v-icon>
          Delete Video
        </v-card-title>
        <v-card-text>
          <p>Are you sure you want to delete <strong>{{ deleteTarget?.originalName }}</strong>?</p>
          <p class="text-error text-body-2">
            This will permanently delete the video, all {{ deleteTarget?.frameCount }} frames and all labels.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="deleting" @click="handleDelete">
            <v-icon start>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Training Config Dialog -->
    <v-dialog v-model="showTrainDialog" max-width="600">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="info" class="mr-2">mdi-brain</v-icon>
          Start YOLO Training
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            This will train the dice detection model using all exported labels.
            Make sure you've run Export YOLO first.
          </v-alert>
          <v-row>
            <v-col cols="6">
              <v-select
                v-model="trainModel"
                :items="['yolov8n.pt', 'yolov8s.pt', 'yolov8m.pt']"
                label="Base model"
                hint="n=nano (fast), s=small, m=medium (accurate)"
                persistent-hint
              />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model.number="trainEpochs" label="Epochs" type="number" min="10" max="1000" />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model.number="trainBatchSize" label="Batch size" type="number" min="1" max="64" />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model.number="trainImageSize" label="Image size" type="number" min="320" max="1280" step="32" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showTrainDialog = false">Cancel</v-btn>
          <v-btn color="info" variant="flat" :loading="startingTraining" @click="handleStartTraining">
            <v-icon start>mdi-play</v-icon>
            Start
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Training Log Dialog -->
    <v-dialog v-model="showTrainLog" max-width="800" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-console</v-icon>
          Training Log
          <v-spacer />
          <v-chip v-if="trainingRunning" size="small" color="info" variant="tonal">
            <v-progress-circular indeterminate size="12" width="2" class="mr-1" />
            Running
          </v-chip>
          <v-chip v-else-if="trainingExitCode === 0" size="small" color="success" variant="tonal">Complete</v-chip>
          <v-chip v-else-if="trainingExitCode !== null" size="small" color="error" variant="tonal">Failed</v-chip>
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 500px; overflow-y: auto;">
          <pre class="training-log">{{ trainingLog.join('\n') }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="trainingRunning" color="error" variant="tonal" :loading="stoppingTraining" @click="handleStopTraining">
            <v-icon start>mdi-stop</v-icon>
            Stop
          </v-btn>
          <v-spacer />
          <v-btn variant="text" @click="showTrainLog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import {
  getVideos,
  registerVideo,
  processVideo,
  updateVideo,
  deleteVideo,
  getFrames,
  exportLabels,
  getTrainingStatus,
  startTraining,
  stopTraining,
  runTest,
  getTestImageUrl,
} from '../../services/trainingApi';

const diceColors = ['green', 'blue', 'orange', 'red', 'purple', 'pink'];

// ─── Video & Stats State ────────────────────────────────────
const videos = ref([]);
const pendingFiles = ref([]);
const stats = ref(null);
const loadingVideos = ref(false);
const exporting = ref(false);
const registeringFile = ref(null);
const processingVideoId = ref(null);
const deleting = ref(false);

// ─── Video Dialogs ──────────────────────────────────────────
const showProcessDialog = ref(false);
const processTarget = ref(null);
const processFps = ref(1.0);
const showDeleteDialog = ref(false);
const deleteTarget = ref(null);

// ─── Training State ─────────────────────────────────────────
const showTrainDialog = ref(false);
const showTrainLog = ref(false);
const trainModel = ref('yolov8n.pt');
const trainEpochs = ref(100);
const trainBatchSize = ref(16);
const trainImageSize = ref(640);
const startingTraining = ref(false);
const stoppingTraining = ref(false);
const trainingRunning = ref(false);
const trainingLog = ref([]);
const trainingExitCode = ref(null);
const trainingModel = ref('');
const trainingEpochs = ref(0);
const trainingStartedAt = ref(null);
let trainingPollInterval = null;

// ─── Test State ─────────────────────────────────────────────
const testing = ref(false);
const testCount = ref(10);
const testConfidence = ref(0.25);
const testResults = ref([]);

// ─── Snackbar ───────────────────────────────────────────────
const snackbar = ref(false);
const snackbarText = ref('');
const snackbarColor = ref('success');

function showSnack(text, color = 'success') {
  snackbarText.value = text;
  snackbarColor.value = color;
  snackbar.value = true;
}

// ─── Helpers ────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '–';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function statusColor(status) {
  switch (status) {
    case 'pending': return 'grey';
    case 'processing': return 'blue';
    case 'extracted': return 'info';
    case 'completed': return 'success';
    case 'error': return 'error';
    default: return 'grey';
  }
}

function statusLabel(status) {
  switch (status) {
    case 'pending': return 'Pending';
    case 'processing': return 'Processing...';
    case 'extracted': return 'Frames ready';
    case 'completed': return 'Completed';
    case 'error': return 'Error';
    default: return status;
  }
}

function getTestImageSrc(filename) {
  return getTestImageUrl(filename);
}

// ─── Load Data ──────────────────────────────────────────────

async function loadVideos() {
  loadingVideos.value = true;
  try {
    const { data } = await getVideos();
    videos.value = data.videos || [];
    pendingFiles.value = data.pendingFiles || [];
  } catch (err) {
    showSnack('Could not fetch videos: ' + (err.response?.data?.error || err.message), 'error');
  }
  loadingVideos.value = false;
}

async function loadStats() {
  try {
    const { data } = await getFrames();
    stats.value = data.stats || null;
  } catch {}
}

// ─── Register Video ─────────────────────────────────────────

async function handleRegister(filename) {
  registeringFile.value = filename;
  try {
    const { data } = await registerVideo(filename);
    showSnack(`Video registered: ${filename} (ID: ${data.videoId})`);
    await loadVideos();
  } catch (err) {
    showSnack('Registration failed: ' + (err.response?.data?.error || err.message), 'error');
  }
  registeringFile.value = null;
}

// ─── Process Video ──────────────────────────────────────────

function openProcessDialog(video) {
  processTarget.value = video;
  processFps.value = video.fps || 1.0;
  showProcessDialog.value = true;
}

async function handleProcess() {
  if (!processTarget.value) return;
  processingVideoId.value = processTarget.value.videoId;
  showProcessDialog.value = false;

  try {
    const { data } = await processVideo(processTarget.value.videoId, processFps.value);
    showSnack(`${data.frameCount} frames extracted from ${processTarget.value.originalName}`);
    await loadVideos();
    await loadStats();
  } catch (err) {
    showSnack('Extraction failed: ' + (err.response?.data?.error || err.message), 'error');
    await loadVideos();
  }
  processingVideoId.value = null;
  processTarget.value = null;
}

// ─── Mark Completed ─────────────────────────────────────────

async function handleMarkCompleted(video) {
  try {
    await updateVideo(video.videoId, { status: 'completed' });
    showSnack(`${video.originalName} marked as completed`);
    await loadVideos();
  } catch (err) {
    showSnack('Error: ' + (err.response?.data?.error || err.message), 'error');
  }
}

// ─── Delete Video ───────────────────────────────────────────

function openDeleteDialog(video) {
  deleteTarget.value = video;
  showDeleteDialog.value = true;
}

async function handleDelete() {
  if (!deleteTarget.value) return;
  deleting.value = true;
  try {
    await deleteVideo(deleteTarget.value.videoId);
    showSnack(`${deleteTarget.value.originalName} deleted`);
    showDeleteDialog.value = false;
    deleteTarget.value = null;
    await loadVideos();
    await loadStats();
  } catch (err) {
    showSnack('Deletion failed: ' + (err.response?.data?.error || err.message), 'error');
  }
  deleting.value = false;
}

// ─── Export YOLO ────────────────────────────────────────────

async function handleExport() {
  exporting.value = true;
  try {
    const { data } = await exportLabels(0.8);
    showSnack(`Exported ${data.savedCount} frames (${data.trainCount} train / ${data.valCount} val, ${data.totalBoxes} boxes)`);
  } catch (err) {
    showSnack('Export failed: ' + (err.response?.data?.error || err.message), 'error');
  }
  exporting.value = false;
}

// ─── Training ───────────────────────────────────────────────

async function pollTrainingStatus() {
  try {
    const { data } = await getTrainingStatus();
    trainingRunning.value = data.running;
    trainingLog.value = data.log || [];
    trainingExitCode.value = data.exitCode;
    trainingModel.value = data.model || '';
    trainingEpochs.value = data.epochs || 0;
    trainingStartedAt.value = data.startedAt || null;

    if (!data.running && trainingPollInterval) {
      clearInterval(trainingPollInterval);
      trainingPollInterval = null;
      if (data.exitCode === 0) {
        showSnack('Training completed successfully!');
      } else if (data.exitCode !== null) {
        showSnack('Training failed — check the log', 'error');
      }
    }
  } catch {}
}

async function handleStartTraining() {
  startingTraining.value = true;
  try {
    await startTraining({
      model: trainModel.value,
      epochs: trainEpochs.value,
      batchSize: trainBatchSize.value,
      imageSize: trainImageSize.value,
    });
    showTrainDialog.value = false;
    showTrainLog.value = true;
    trainingRunning.value = true;

    if (trainingPollInterval) clearInterval(trainingPollInterval);
    trainingPollInterval = setInterval(pollTrainingStatus, 3000);
    showSnack('Training started');
  } catch (err) {
    showSnack('Failed to start training: ' + (err.response?.data?.error || err.message), 'error');
  }
  startingTraining.value = false;
}

async function handleStopTraining() {
  stoppingTraining.value = true;
  try {
    await stopTraining();
    showSnack('Training stopped');
    await pollTrainingStatus();
  } catch (err) {
    showSnack('Failed to stop: ' + (err.response?.data?.error || err.message), 'error');
  }
  stoppingTraining.value = false;
}

// ─── Test Model ─────────────────────────────────────────────

async function handleRunTest() {
  testing.value = true;
  testResults.value = [];
  try {
    const { data } = await runTest({
      count: testCount.value,
      confidence: testConfidence.value,
    });
    testResults.value = data.resultImages || [];
    showSnack(`Tested ${data.testedFrames} frames — ${testResults.value.length} results`);
  } catch (err) {
    showSnack('Test failed: ' + (err.response?.data?.error || err.message), 'error');
  }
  testing.value = false;
}

// ─── Lifecycle ──────────────────────────────────────────────

onMounted(() => {
  loadVideos();
  loadStats();
  pollTrainingStatus();
});

onUnmounted(() => {
  if (trainingPollInterval) {
    clearInterval(trainingPollInterval);
    trainingPollInterval = null;
  }
});
</script>

<style scoped>
.training-log {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  color: #ccc;
  background: #111;
  padding: 12px;
  border-radius: 6px;
  margin: 0;
}
.test-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}
.test-image-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
}
.test-image {
  width: 100%;
  height: auto;
  display: block;
}
.test-image-name {
  padding: 4px 8px;
  font-size: 10px;
  color: #888;
  background: #111;
}
</style>